import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, mergeMap, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CreatePasswordBody {
  email: string;
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ApiEnvelope<T = unknown> {
  statusCode: number;
  status: boolean;
  message: string;
  data: T;
  timestamp?: string;
  path?: string;
}

export interface AdminLoginMfaData {
  mfa_required?: boolean;
  userData?: {
    id: number;
    email_id: string;
    full_name: string | null;
    initials?: string;
    profile_picture?: unknown;
    status?: string;
  };
}

export interface AdminVerifyMfaData {
  userData?: AdminLoginMfaData['userData'];
}

@Injectable({ providedIn: 'root' })
export class SuperAdminAuthService {
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  adminLogin(email_id: string, password: string): Observable<ApiEnvelope<AdminLoginMfaData>> {
    return this.http.post<ApiEnvelope<AdminLoginMfaData>>(
      `${this.base}/auth/admin/login`,
      { email_id, password },
    );
  }

  verifyAdminMfa(
    email_id: string,
    otp: string,
  ): Observable<ApiEnvelope<AdminVerifyMfaData>> {
    return this.http.post<ApiEnvelope<AdminVerifyMfaData>>(
      `${this.base}/auth/admin/verify-mfa`,
      { email_id, otp },
    );
  }

  logout(): Observable<ApiEnvelope<unknown>> {
    return this.http.post<ApiEnvelope<unknown>>(
      `${this.base}/auth/admin/logout`,
      {},
    );
  }

  /** Request password reset email (`ForgotPasswordDto.email_id`). Public — no JWT. */
  forgotPassword(email_id: string): Observable<ApiEnvelope<unknown>> {
    return this.http.post<ApiEnvelope<unknown>>(`${this.base}/auth/forgot-password`, {
      email_id,
    });
  }

  /** Validate reset token before showing new-password form (`GET auth/password-token`). Public. */
  getPasswordResetUser(token: string): Observable<ApiEnvelope<Record<string, unknown>>> {
    return this.http.get<ApiEnvelope<Record<string, unknown>>>(
      `${this.base}/auth/password-token`,
      { params: { token } },
    );
  }

  /** Set new password using token from email (`PATCH auth/create-password`). Public. */
  createPassword(body: CreatePasswordBody): Observable<ApiEnvelope<unknown>> {
    return this.http.patch<ApiEnvelope<unknown>>(`${this.base}/auth/create-password`, body);
  }

  /** Confirm session still valid (cookie sent via interceptor). Fails if HTTP error or envelope reports failure. */
  pingProfile(): Observable<unknown> {
    return this.http.get<ApiEnvelope<unknown>>(`${this.base}/user/admin/profile`).pipe(
      mergeMap((r) => {
        if (r?.status !== true || (r.statusCode != null && r.statusCode >= 400)) {
          return throwError(() => new Error('Not authenticated'));
        }
        return of(r.data);
      }),
    );
  }
}
